// .ts extensions: this module is in the node --test import graph, which resolves literally
import { combineLinkWithBucket } from '../api/cdn.ts';
import type { QueryParams } from '../api/core/params';
import type { ContentUnit } from '../board/panels/lectureFacts';
import { searchSlug } from '../library/slug.ts';
import { msg, type MaybeMsg } from '../i18n/msg.ts';

// Courses on IZTeach online-course (public, no IZ sign-in needed); only card/outline fields are typed here

type Ref = { id?: string; _id?: string } | string;

export interface IztCourse {
  id?: string;
  _id?: string;
  title?: string | null;
  previewImg?: string | null;
  topics?: Array<Ref & { title?: string | null }> | null;
  authors?: Array<Ref & { fullname?: string | null; name?: string | null }> | null;
  meta?: { isMaster?: unknown } | null;
  isArchived?: unknown;
}

export interface IztLayoutUnit {
  id?: string;
  _id?: string;
  title?: string | null;
  index?: number | null;
  type?: string | null;
  settings?: { isDraft?: unknown; homeworkQuestion?: unknown } | null;
  data?: { homeworkQuestion?: unknown } | null;
}

/** One chapter of GET /course/layout/{id}; sub-sections only exist when the course has level-1 sections. */
export interface IztLayoutSection {
  id?: string;
  _id?: string;
  title?: string | null;
  index?: number | null;
  children?: IztLayoutUnit[] | null;
  childSections?: IztLayoutSection[] | null;
}

export interface CourseCard {
  id: string;
  /** The course's own title; '' when it has none (render it with courseTitle, which names it in the current language). */
  title: string;
  cover: string | null;
  /** Topic titles as the course carries them, e.g. "Toán • Khối 9". */
  topics: string;
  teachers: string[];
}

/** Units under one heading; name is null for units filed straight on the chapter. */
export interface CourseLesson {
  id: string;
  name: string | null;
  units: ContentUnit[];
}

/** Display names: data keeps the raw title, and an untitled one resolves to a Msg the screen translates at render (useMsg). */
export function courseTitle(card: { title: string }): MaybeMsg {
  return card.title || msg('courses:untitled.course');
}

export function unitName(unit: { name: string }): MaybeMsg {
  return unit.name || msg('courses:untitled.unit');
}

export function chapterName(chapter: { name: string }): MaybeMsg {
  return chapter.name || msg('courses:untitled.chapter');
}

export interface CourseChapter {
  id: string;
  name: string;
  lessons: CourseLesson[];
}

export const COURSE_PAGE_SIZE = 24;

function refId(ref: { id?: string; _id?: string }): string {
  return String(ref.id || ref._id || '');
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** One catalog page. No type filter: courses QLT's sync-izt inserts carry type null. */
export function catalogQuery(search: string, skip: number, limit: number = COURSE_PAGE_SIZE): QueryParams {
  const query: QueryParams = { _limit: limit, _skip: skip };
  const slug = searchSlug(search);
  if (slug) query.tags_contain = slug;
  return query;
}

/** The count reads the same filter minus paging. */
export function catalogFilter(search: string): QueryParams {
  const slug = searchSlug(search);
  return slug ? { tags_contain: slug } : {};
}

/** A master course is the chapter framework Giao bài copies from and holds no lessons. */
export function isMasterCourse(course: IztCourse): boolean {
  return Boolean(course.meta && course.meta.isMaster === true);
}

export function courseCard(course: IztCourse): CourseCard | null {
  const id = refId(course);
  if (!id) return null;
  const topics: string[] = [];
  (course.topics || []).forEach((topic) => {
    const title = typeof topic === 'string' ? '' : text(topic.title);
    if (title && topics.indexOf(title) < 0) topics.push(title);
  });
  const teachers: string[] = [];
  (course.authors || []).forEach((author) => {
    const name = typeof author === 'string' ? '' : text(author.fullname) || text(author.name);
    if (name && teachers.indexOf(name) < 0) teachers.push(name);
  });
  const cover = text(course.previewImg);
  return {
    id,
    title: text(course.title),
    cover: cover ? combineLinkWithBucket(cover) : null,
    topics: topics.join(' • '),
    teachers,
  };
}

/** The catalog's own listing filters isArchived on the server; a lookup by id has to do it here. */
export function isArchivedCourse(course: IztCourse): boolean {
  return course.isArchived === true;
}

/** The card another screen hands over in router state to open that course directly; anything else is ignored. */
export function openedCourse(state: unknown): CourseCard | null {
  const course = state && typeof state === 'object' ? (state as { course?: unknown }).course : null;
  if (!course || typeof course !== 'object') return null;
  const card = course as Partial<CourseCard>;
  if (typeof card.id !== 'string' || !card.id || typeof card.title !== 'string') return null;
  return {
    id: card.id,
    title: card.title,
    cover: typeof card.cover === 'string' && card.cover ? card.cover : null,
    topics: typeof card.topics === 'string' ? card.topics : '',
    teachers: Array.isArray(card.teachers) ? card.teachers.filter((name): name is string => typeof name === 'string') : [],
  };
}

/** Cards for one page, plus how many masters it dropped so the total can own up to them. */
export function catalogPage(rows: IztCourse[]): { cards: CourseCard[]; hidden: number } {
  const cards: CourseCard[] = [];
  let hidden = 0;
  rows.forEach((row) => {
    const card = isMasterCourse(row) ? null : courseCard(row);
    if (card) cards.push(card);
    else hidden += 1;
  });
  return { cards, hidden };
}

function byIndex<T extends { index?: number | null }>(list: T[] | null | undefined): T[] {
  return (list || []).slice().sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0));
}

/** The shape the Kho bài rows render; a draft is not a lesson yet, as the izt-content tree agrees. */
function contentUnits(list: IztLayoutUnit[] | null | undefined): ContentUnit[] {
  const units: ContentUnit[] = [];
  byIndex(list).forEach((unit) => {
    const id = refId(unit);
    if (!id || (unit.settings && unit.settings.isDraft === true)) return;
    const homework = (unit.data && unit.data.homeworkQuestion) || (unit.settings && unit.settings.homeworkQuestion) || null;
    units.push({ id, name: text(unit.title), type: unit.type || null, homeworkQuestion: homework });
  });
  return units;
}

// Chapters in index order (layout sorts by updatedAt, not index); empty chapters/sub-sections are dropped, not shown as dead headings
export function courseOutline(layout: IztLayoutSection[] | null | undefined): CourseChapter[] {
  const chapters: CourseChapter[] = [];
  byIndex(Array.isArray(layout) ? layout : []).forEach((section) => {
    const id = refId(section);
    if (!id) return;
    const lessons: CourseLesson[] = [];
    const direct = contentUnits(section.children);
    if (direct.length > 0) lessons.push({ id, name: null, units: direct });
    byIndex(section.childSections).forEach((child) => {
      const units = contentUnits(child.children);
      if (units.length > 0) lessons.push({ id: refId(child) || `${id}-${lessons.length}`, name: text(child.title) || null, units });
    });
    if (lessons.length > 0) chapters.push({ id, name: text(section.title), lessons });
  });
  return chapters;
}

export function outlineTotals(chapters: CourseChapter[]): { chapters: number; units: number } {
  let units = 0;
  chapters.forEach((chapter) => chapter.lessons.forEach((lesson) => { units += lesson.units.length; }));
  return { chapters: chapters.length, units };
}

/** Where a unit sits, for the detail sheet's Chương row. */
export function unitPlace(chapters: CourseChapter[], unitId: string): MaybeMsg {
  for (let i = 0; i < chapters.length; i += 1) {
    const lessons = chapters[i].lessons;
    for (let j = 0; j < lessons.length; j += 1) {
      if (lessons[j].units.some((unit) => unit.id === unitId)) {
        return lessons[j].name ? msg('courses:place.lesson', { chapter: chapterName(chapters[i]), lesson: lessons[j].name }) : chapterName(chapters[i]);
      }
    }
  }
  return '';
}
